// Keeps the strip player's live item current without polling.
//
// The server states what is on, what follows, and a window in which to come back; this module sets
// one timer to the next boundary and advances locally. GLZ embeds the whole day, so it advances all
// day with zero requests; GLGLZ's feed only knows one song ahead, so it refills once per song.
//
// Three rules make it robust where a naive timer would not be:
//   * the current segment is derived from the clock, never by incrementing a pointer — so a cached
//     page, a resumed laptop or a throttled timer all self-correct;
//   * the timer re-arms in short chunks, because long timers drift and browsers throttle them;
//   * refills happen at a random instant inside the server's window, because every listener shares
//     the same boundary and an unjittered burst is exactly the traffic shape we are avoiding.
const REARM_CHUNK_MS = 10 * 60 * 1000;
const MAX_CLOCK_OFFSET_MS = 120 * 1000;
const BACKOFF_MS = [5000, 15000, 60000, 300000];
// Backstop against a server that hands back an already-open refill window (a lagging feed, a bug):
// without a floor between consecutive refills that becomes an unthrottled request loop. The server
// guards this too — this is the second lock on the same door, because the failure mode is the exact
// traffic pattern the whole design exists to avoid.
const MIN_REFILL_GAP_MS = 20000;
// An unbounded fetch that is accepted but never answered would leave `refilling` true forever,
// silently killing all future refills until a page reload — the same hang-shape that starved this
// site's server thread pools in the 2026-07-12 incident. Abort so the catch path always runs.
const FETCH_TIMEOUT_MS = 8000;

export function installLiveSchedule() {
  const el = document.querySelector('[data-player]');
  const seedEl = document.getElementById('live-schedule');
  if (!el || !seedEl || !window.GLZ || !window.GLZ.player) return;

  const refillUrl = el.dataset.refillUrl || '';
  let segments = [];
  let refill = null;
  let nextProgramme = null;
  let offsetMs = 0;
  let timer = null;
  let refillTimer = null;
  let failures = 0;
  let applied = null;
  let nextAllowedAt = 0;   // earliest instant a refill may run: rate floor and backoff, unified
  let refilling = false;

  // A large apparent skew almost always means the response was cached, which makes serverNowUtc
  // meaningless — discard it rather than clamp, and trust the (NTP-synced) device clock.
  const adoptClock = serverNowUtc => {
    const server = Date.parse(serverNowUtc);
    if (!isFinite(server)) { offsetMs = 0; return; }
    const delta = server - Date.now();
    offsetMs = Math.abs(delta) > MAX_CLOCK_OFFSET_MS ? 0 : delta;
  };

  const now = () => Date.now() + offsetMs;

  const load = data => {
    if (!data || !Array.isArray(data.segments)) return false;
    adoptClock(data.serverNowUtc);
    segments = data.segments
      .map(s => ({
        title: s.title ?? s.Title ?? '',
        desc: s.desc ?? s.Desc ?? '',
        image: s.image ?? s.Image ?? '',
        link: s.link ?? s.Link ?? '',
        startsAt: Date.parse(s.startsUtc ?? s.StartsUtc),
        endsAt: (s.endsUtc ?? s.EndsUtc) ? Date.parse(s.endsUtc ?? s.EndsUtc) : null
      }))
      .filter(s => isFinite(s.startsAt));
    // Same isFinite discipline as the segments and the clock reference. An unparseable window yields
    // NaN, and NaN propagates through Math.max — bypassing the refill floor and scheduling a timeout
    // whose NaN delay browsers treat as 0, i.e. the unthrottled retry loop this module exists to
    // prevent. Fail closed: no window means no refills, degrading to a correct-but-frozen strip
    // rather than a request storm.
    const w = data.refillWindow ?? data.RefillWindow ?? null;
    const startAt = w ? Date.parse(w.startUtc ?? w.StartUtc) : NaN;
    const jitterSeconds = Number(w ? (w.jitterSeconds ?? w.JitterSeconds ?? 0) : 0);
    refill = isFinite(startAt) && isFinite(jitterSeconds)
      ? { startAt, jitterSeconds: Math.max(0, jitterSeconds) }
      : null;
    const p = data.nextProgramme ?? data.NextProgramme ?? null;
    nextProgramme = p
      ? { title: p.title ?? p.Title ?? '', desc: p.desc ?? p.Desc ?? '', image: p.image ?? p.Image ?? '', link: p.link ?? p.Link ?? '' }
      : null;
    return true;
  };

  // An open-ended segment (null endsAt) runs until the next refill closes it.
  const indexAt = t => segments.findIndex(s => s.startsAt <= t && (s.endsAt === null || t < s.endsAt));

  // GLGLZ's next panel pairs the next distinct programme (nextProgramme, from the timetable) with
  // the upcoming song (the following segment). Without nextProgramme the following segment IS the
  // next programme, as on GLZ. Right after a boundary the next song is unknown (the feed reports one
  // song ahead, and lags) — fall back to the programme's own subtitle, not a blank.
  const nextFor = i => {
    const seg = segments[i + 1] || null;
    if (!nextProgramme) return seg;
    return {
      title: nextProgramme.title,
      desc: (seg && seg.desc) || nextProgramme.desc,
      image: nextProgramme.image || (seg ? seg.image : ''),
      link: nextProgramme.link || (seg ? seg.link : '')
    };
  };

  const apply = () => {
    const i = indexAt(now());
    if (i < 0) return;                       // in a gap — leave the server-rendered fallback showing
    if (applied === segments[i]) return;     // already displayed; don't churn the store
    applied = segments[i];
    window.GLZ.player.setLiveNow(segments[i], nextFor(i));
  };

  const arm = () => {
    clearTimeout(timer);
    const i = indexAt(now());
    const boundary = i >= 0 && segments[i].endsAt !== null ? segments[i].endsAt
      : (i < 0 ? nextStartAfter(now()) : null);
    const delay = boundary === null ? REARM_CHUNK_MS : Math.max(0, boundary - now());
    timer = setTimeout(tick, Math.min(delay, REARM_CHUNK_MS));
  };

  const nextStartAfter = t => {
    const upcoming = segments.find(s => s.startsAt > t);
    return upcoming ? upcoming.startsAt : null;
  };

  const tick = () => { apply(); arm(); };

  const scheduleRefill = () => {
    clearTimeout(refillTimer);
    if (!refill || !refillUrl) return;
    const jitter = Math.random() * refill.jitterSeconds * 1000;
    const at = Math.max(refill.startAt + jitter, nextAllowedAt);
    refillTimer = setTimeout(doRefill, Math.max(0, at - now()));
  };

  // Hidden tabs never refill; they catch up on becoming visible. This is what takes a backgrounded
  // listener's request count to zero. The in-flight guard and the clearTimeout matter because wake()
  // can call this directly while a jittered refill or a backoff retry is already armed.
  const doRefill = async () => {
    if (document.hidden || refilling) return;

    // The rate floor and the backoff are enforced HERE, not only in scheduleRefill(), because wake()
    // reaches this directly. Without it, a user toggling tab focus during an outage issues one
    // request per focus event — unthrottled retries, the exact traffic shape this module prevents.
    if (now() < nextAllowedAt) { scheduleRefill(); return; }

    clearTimeout(refillTimer);
    refilling = true;

    const abort = new AbortController();
    const timeoutId = setTimeout(() => abort.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(refillUrl, { headers: { Accept: 'application/json' }, signal: abort.signal });
      if (!res.ok) throw new Error(res.status);
      if (!load(await res.json())) throw new Error('bad payload');
      // Apply the freshly loaded segments regardless of what follows — the data is good even when the
      // window below is stale, and the strip must not freeze waiting on the window to look right.
      apply(); arm();
      // A successful load lands in one of three buckets, distinguished by the refill window:
      //   - dead feed: never reaches here — handled server-side, routed to the timetable fallback.
      //   - malformed window: load() already set refill = null (fail closed). scheduleRefill() below
      //     is then a no-op (it requires `refill`), so this is a normal reset with no refill armed —
      //     correct, since a malformed window gives us nothing to schedule against.
      //   - stale-but-serving feed: refill is present but its startAt is already <= now(), meaning the
      //     feed keeps reporting a boundary that already passed and the server could not advance the
      //     window — an immediate re-ask would learn nothing new. Treat this as a soft failure and
      //     climb the same BACKOFF_MS ladder as a network failure, instead of accepting the flat
      //     MIN_REFILL_GAP_MS floor — otherwise every listener refills every 20-30s for as long as the
      //     feed stays stale, which is exactly the polling traffic shape this module exists to remove.
      if (refill && refill.startAt <= now()) {
        const wait = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
        failures += 1;
        nextAllowedAt = now() + wait;
        refillTimer = setTimeout(doRefill, wait);
      } else {
        failures = 0;
        nextAllowedAt = now() + MIN_REFILL_GAP_MS;
        scheduleRefill();
      }
    } catch (e) {
      // Keep the last known segments on screen — never blank a title that was correct a moment ago.
      const wait = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
      failures += 1;
      nextAllowedAt = now() + wait;
      refillTimer = setTimeout(doRefill, wait);
    } finally {
      clearTimeout(timeoutId);
      refilling = false;
    }
  };

  const wake = () => {
    if (document.hidden) return;
    apply();
    arm();
    if (refill && now() >= refill.startAt) doRefill(); else scheduleRefill();
  };

  try {
    if (!load(JSON.parse(seedEl.textContent))) return;
  } catch (e) {
    return;
  }

  apply();
  arm();
  scheduleRefill();

  // Timers alone are not enough: background tabs throttle them to roughly once a minute and
  // suspended devices do not fire them at all. Re-deriving is pure arithmetic, so waking is free.
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('pageshow', wake);
  window.addEventListener('focus', wake);
}
